import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { configureFormatting } from '@/lib/format';

const DEFAULT_BRANDING = {
  company_name: 'FPCCS', application_name: 'FPCCS',
  tagline: 'Project Cost Control System', primary_color: '#6366F1', logo_url: '',
  sidebar_logo_url: '', login_logo_url: '', favicon_url: '', login_background_url: '',
  footer_text: '', copyright_text: '', contact_email: null, contact_phone: '', website: '',
  timezone: 'Asia/Jakarta', currency: 'IDR', language: 'id', default_theme: 'system', branding_version: 1,
};
const BrandingContext = createContext({ branding: DEFAULT_BRANDING, refresh: async () => {}, save: async () => {} });

function applyBranding(branding) {
  // Apply the profile together with its persisted branding values.  ThemeContext
  // also owns the light/dark preference, but setting this here makes a profile
  // switch take effect immediately after a successful save.
  document.documentElement.dataset.themeProfile = branding.theme_profile === 'forte' ? 'forte' : 'modern';
  document.documentElement.style.setProperty('--fpccs-primary', branding.primary_color);
  const hex = String(branding.primary_color || '#6366F1').replace('#', '');
  const rgb = [0, 2, 4].map((offset) => parseInt(hex.slice(offset, offset + 2), 16));
  if (rgb.every(Number.isFinite)) {
    document.documentElement.style.setProperty('--fpccs-primary-rgb', rgb.join(', '));
    const [r, g, b] = rgb.map((value) => value / 255);
    const max = Math.max(r, g, b), min = Math.min(r, g, b), delta = max - min;
    let hue = 0;
    if (delta) hue = max === r ? 60 * (((g - b) / delta) % 6) : max === g ? 60 * ((b - r) / delta + 2) : 60 * ((r - g) / delta + 4);
    if (hue < 0) hue += 360;
    const light = (max + min) / 2;
    const saturation = delta ? delta / (1 - Math.abs(2 * light - 1)) : 0;
    const hsl = `${Math.round(hue)} ${Math.round(saturation * 100)}% ${Math.round(light * 100)}%`;
    document.documentElement.style.setProperty('--primary', hsl);
    document.documentElement.style.setProperty('--ring', hsl);
    document.documentElement.style.setProperty('--chart-1', hsl);
  }
  document.documentElement.lang = branding.language || 'id';
  configureFormatting(branding);
  document.title = `${branding.application_name} — ${branding.company_name}`;
  let favicon = document.querySelector("link[rel='icon']");
  if (!favicon) {
    favicon = document.createElement('link');
    favicon.rel = 'icon';
    document.head.appendChild(favicon);
  }
  favicon.href = branding.favicon_url || branding.sidebar_logo_url || branding.logo_url || '/favicon.ico';
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const refresh = async () => {
    try { setBranding({ ...DEFAULT_BRANDING, ...(await api.get('/branding')).data }); } catch (_) { setBranding(DEFAULT_BRANDING); }
  };
  const save = async (values) => {
    const updated = { ...DEFAULT_BRANDING, ...(await api.put('/branding', values)).data };
    setBranding(updated);
    return updated;
  };
  useEffect(() => { refresh(); }, []);
  useEffect(() => { applyBranding(branding); }, [branding]);
  const value = useMemo(() => ({ branding, refresh, save }), [branding]);
  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export const useBranding = () => useContext(BrandingContext);
